import React, { useEffect, useState } from 'react';
import { useRouter } from 'next/navigation';
import toast from 'react-hot-toast';
import Button from '../../ui/Button';
import ActivityIcon from '../../ActivityIcon';
import { CheckCircleIcon } from '@/assets/ui/activity';
import { QuizitIcon } from '@/assets/ui/study';
import { ActivityData } from 'course-types';
import ActivityUtilityService from '@/services/course/lesson/activity/activity-utility-service';
import AuroraUtilityService from '@/services/util/aurora-utililty-service';
import UtilityService from '@/services/util/utility-service';
import { useFlashcardGeneration } from '@/common/hooks/useFlashcardGeneration';
import { useAppDispatch, useAppSelector, useAppStore } from '@/lib/hooks';

interface OutlineButtonProps {
  text: string;
  onClick: () => void;
}

function OutlineButton({ text, onClick }: OutlineButtonProps): React.JSX.Element {
  return (
    <Button
      onClick={onClick}
      className='bg-none text-primary border border-primary rounded-full py-1 pl-3 pr-[10px] h-fit flex items-center justify-center gap-1'
      outline
    >
      <QuizitIcon className='text-primary size-[12px]' />
      <div className='text-primary text-xs font-normal text-nowrap'>{text}</div>
    </Button>
  );
}

interface StudentActivityInfoProps {
  activity: ActivityData;
  courseId: string;
  unitId: string;
  subunitId: string | undefined;
}

export function StudentActivityInfo({
  activity,
  courseId,
  unitId,
  subunitId,
}: StudentActivityInfoProps): React.JSX.Element {
  const router = useRouter();
  const dispatch = useAppDispatch();
  const store = useAppStore();

  const currentQuizQuestions = useAppSelector(() => store.getState().quiz.currentQuizQuestions);
  const quizJustSubmitted = useAppSelector(() => store.getState().quiz.justSubmitted);

  const [isCompleted, setIsCompleted] = useState<boolean>(false);

  const { handleGenerateFlashcards } = useFlashcardGeneration();

  useEffect(() => {
    if (!activity.id || !courseId || !unitId) return;

    try {
      const { activityData } = ActivityUtilityService.retrieveActivityProgressLocal(
        courseId,
        unitId,
        subunitId,
      );

      if (activity.type === 'video') {
        setIsCompleted(!!activityData.video[activity.id]?.submissionDate);
      } else if (activity.type === 'document') {
        setIsCompleted(!!activityData.document[activity.id]?.submissionDate);
      } else if (activity.type === 'assignment') {
        setIsCompleted(!!activityData.assignment?.[activity.id]?.submissionDate);
      } else if (activity.type === 'quiz') {
        setIsCompleted(quizJustSubmitted);
      }
    } catch {
      setIsCompleted(false);
      toast.error('Failed to load activity progress. Please refresh the page.', { duration: 3000 });
    }
  }, [activity.id, activity.type, courseId, unitId, subunitId, quizJustSubmitted]);

  const handleMarkComplete = async () => {
    try {
      if (isCompleted) return;
      if (activity.type !== 'video' && activity.type !== 'document') return;
      await ActivityUtilityService.onActivityComplete(
        courseId,
        unitId,
        subunitId,
        activity.id,
        activity.type,
      );
      setIsCompleted(true);
    } catch (error) {
      toast.error('Failed to mark activity as complete. Try again.', { duration: 3000 });
    }
  };

  const handleTakeSelfQuiz = () => {
    void AuroraUtilityService.generateDocumentQuiz(
      activity.id,
      subunitId,
      unitId,
      dispatch,
      activity.name,
    );
    router.push('/student/study/self-quiz');
  };

  const handleGenerateFlashcardsClick = () => {
    handleGenerateFlashcards(currentQuizQuestions, activity.name);
  };

  return (
    <div className='flex flex-row gap-3 px-8 items-center justify-center'>
      <ActivityIcon type={activity.type} border />
      <div className='flex flex-col item items-start gap-1 grow'>
        <div className='flex flex-row item items-center gap-2'>
          <div className='py-0 w-fit text-2xl font-semibold text-text_gray_900'>
            {UtilityService.cleanInvalidWhitespace(activity.name ?? '')}
          </div>
          <div className='rounded-full bg-surface_gray_300 px-2 py-1 text-sm text-text_gray_700'>
            {UtilityService.capitalize(activity.type)}
          </div>
        </div>
      </div>
      <div className='flex flex-col items-end gap-2'>
        {(activity.type === 'document' || (activity.type === 'video' && isCompleted)) && (
          <Button
            onClick={handleMarkComplete}
            className='bg-blue-600 text-white rounded-full py-1 pl-3 pr-[10px] h-fit flex items-center justify-center gap-1'
          >
            <CheckCircleIcon className='stroke-current size-[12px]' />
            <div className='text-white text-xs font-normal text-nowrap'>
              {isCompleted ? 'Completed' : 'Mark as complete'}
            </div>
          </Button>
        )}
        {activity.type === 'document' && isCompleted && (
          <OutlineButton text='Take a Quiz' onClick={handleTakeSelfQuiz} />
        )}
        {activity.type === 'quiz' && isCompleted && (
          <OutlineButton text='Generate Flashcards' onClick={handleGenerateFlashcardsClick} />
        )}
      </div>
    </div>
  );
}
